/**
 * Fetch-a-ticker panel (v1.5.0): the works-out-of-the-box entry point. Type a
 * symbol, click Fetch, and the backend resolves-or-creates the company, pulls
 * prices + fundamentals from the best available provider, and caches them
 * locally. Shows a per-capability freshness report (source, records stored,
 * whether it's cached/stale) and hands the result back so the list refreshes.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/api/client";
import type { CapabilityReport, IngestionReport } from "@/types";

function ReportRow({ label, report }: { label: string; report: CapabilityReport | null }): React.ReactElement {
  if (!report) {
    return (
      <div className="flex items-center justify-between text-xs">
        <span className="text-mist-300">{label}</span>
        <span className="text-mist-500">not attempted</span>
      </div>
    );
  }
  const color = !report.available ? "text-signal-rose" : report.stale ? "text-signal-amber" : "text-signal-teal";
  const state = !report.available ? "unavailable" : report.stale ? "cached (stale)" : "fresh";
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      <div className="flex items-center justify-between">
        <span className="text-mist-300">{label}</span>
        <span className={color}>
          {state}
          {report.source ? ` · ${report.source}` : ""}
          {report.records ? ` · ${report.records} new` : ""}
        </span>
      </div>
      {report.note && <span className="text-mist-500">{report.note}</span>}
    </div>
  );
}

export default function SyncTickerPanel({
  onSynced,
  title = "Research a ticker",
  blurb = "Fetch real fundamentals and prices from the built-in free sources (SEC EDGAR + Stooq) — no setup or API keys. Data is stored locally for analysis, forecasting, and backtesting.",
}: {
  onSynced?: (report: IngestionReport) => void;
  title?: string;
  blurb?: string;
}): React.ReactElement {
  const [symbol, setSymbol] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<IngestionReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchTicker = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const ticker = symbol.trim().toUpperCase();
    if (!ticker) return;
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const { data } = await apiClient.post<IngestionReport>(`/providers/sync/${ticker}`);
      setReport(data);
      onSynced?.(data);
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data
          ?.detail ?? (err as { message?: string })?.message;
      setError(
        detail
          ? String(detail)
          : `Couldn't fetch ${ticker}. Free sources cover US-listed companies only.`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel p-6">
      <h2 className="text-lg font-semibold text-mist-100">{title}</h2>
      <p className="mt-1 text-sm text-mist-400">{blurb}</p>
      <form onSubmit={fetchTicker} className="mt-4 flex flex-wrap items-center gap-2">
        <input
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          className="field-input flex-1 uppercase"
          placeholder="e.g. AAPL"
          aria-label="Ticker symbol"
        />
        <button type="submit" disabled={busy} className="btn-primary">
          {busy ? "Fetching…" : "Fetch data"}
        </button>
      </form>

      {error && <p className="mt-3 text-sm text-signal-rose">{error}</p>}

      {report && (
        <div className="mt-4 space-y-2 rounded-lg border border-ink-700 bg-ink-800/50 p-4">
          <div className="flex items-center justify-between">
            <span className="font-mono text-sm font-semibold text-signal-teal">{report.symbol}</span>
            {report.company_id && (
              <Link to={`/research/${report.company_id}`} className="text-xs text-signal-teal hover:underline">
                Open report →
              </Link>
            )}
          </div>
          <ReportRow label="Fundamentals" report={report.fundamentals} />
          <ReportRow label="Prices" report={report.prices} />
          {report.errors.length > 0 && (
            <p className="text-xs text-signal-rose">{report.errors.join("; ")}</p>
          )}
        </div>
      )}
    </div>
  );
}
